import { useEffect } from 'react';

/**
 * One document-level listener that gives every `.hover-lift` / `[data-tilt]` card a
 * gentle 3D tilt and a light glare that follows the pointer. No per-card code needed,
 * so existing dashboard cards get it for free. Off for touch, coarse pointers and
 * prefers-reduced-motion. Max tilt is small (6°) so numbers stay readable.
 */
const SELECTOR = '.hover-lift, [data-tilt]';
const MAX_DEG = 6;

export default function useTilt3D() {
  useEffect(() => {
    const fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduce) return undefined;

    let active = null;
    let frame = 0;
    let last = null;

    const reset = (el) => {
      if (!el) return;
      el.classList.remove('tilt-active');
      el.style.removeProperty('--tilt-rx');
      el.style.removeProperty('--tilt-ry');
    };

    const apply = () => {
      frame = 0;
      if (!last) return;
      const el = last.target.closest ? last.target.closest(SELECTOR) : null;
      if (el !== active) { reset(active); active = el; }
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const px = (last.clientX - r.left) / r.width;   // 0..1
      const py = (last.clientY - r.top) / r.height;
      el.classList.add('tilt-active');
      el.style.setProperty('--tilt-ry', `${((px - 0.5) * 2 * MAX_DEG).toFixed(2)}deg`);
      el.style.setProperty('--tilt-rx', `${((0.5 - py) * 2 * MAX_DEG).toFixed(2)}deg`);
      el.style.setProperty('--glare-x', `${(px * 100).toFixed(1)}%`);
      el.style.setProperty('--glare-y', `${(py * 100).toFixed(1)}%`);
    };

    const onMove = (e) => {
      last = e;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onLeaveWindow = () => { reset(active); active = null; };

    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeaveWindow);
    window.addEventListener('blur', onLeaveWindow);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeaveWindow);
      window.removeEventListener('blur', onLeaveWindow);
      if (frame) cancelAnimationFrame(frame);
      reset(active);
    };
  }, []);
}
